"use client";

import { useAppContext } from "@/context/Context";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/images/logo/logo.webp";
import logoDark from "../../public/images/light/logo/logo-dark.webp";

/**
 * Componente para la recuperación de contraseña.
 * Envía el correo electrónico del usuario para iniciar el proceso de restablecimiento.
 *
 * @returns {JSX.Element} - Página de recuperación de contraseña.
 */
const ForgotPassword = () => {
  const router = useRouter();
  const { isLightTheme } = useAppContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  /**
   * Maneja el envío del formulario.
   * Valida el formulario y envía una petición POST al endpoint de recuperación.
   *
   * @param {Event} e - Evento del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formElement = e.target;
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setIsSubmitting(true);

    try {
      // Enviar solicitud POST al endpoint para recuperar la contraseña.
      const response = await fetch(`${apiUrl}/user/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formElement.email.value }),
      });

      // Desestructurar la respuesta del servidor.
      const { message, detail } = await response.json();

      // Redirigir a la confirmación si la respuesta es exitosa, o a la página de error en caso contrario.
      router.push(
        response.ok
          ? "/recuperar-contraseña/confirmacion"
          : `/error?message=${encodeURIComponent(
              message || detail || "Error restableciendo contraseña."
            )}`
      );
    } catch {
      // En caso de error, redirigir a la página de error.
      router.push("/error");
    }
  };

  return (
    <main className="page-wrapper">
      <div className="signup-area">
        <div className="wrapper">
          <div className="row">
            <div className="col-lg-12 bg-color-blackest left-wrapper">
              <div className="sign-up-box">
                <div className="signup-box-top">
                  <Image
                    src={isLightTheme ? logo : logoDark}
                    width={193}
                    height={50}
                    alt="sign-up logo"
                  />
                </div>
                <div className="signup-box-bottom">
                  <div className="signup-box-content">
                    <form onSubmit={handleSubmit} noValidate>
                      <h3 className="title mb--30">Olvidé contraseña</h3>
                      <p className="b1">
                        Escribe la dirección de correo electrónico vinculada a
                        tu cuenta de TutorIA+ y te enviaremos un correo para
                        crear una nueva contraseña.
                      </p>
                      <div className="input-section mail-section">
                        <div className="icon">
                          <i className="fa-sharp fa-regular fa-envelope"></i>
                        </div>
                        <input
                          type="email"
                          name="email"
                          maxLength={100}
                          placeholder="Correo electrónico"
                          required
                          title="Introduce un correo electrónico válido."
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn-default"
                        disabled={isSubmitting}
                      >
                        Enviar enlace
                        {isSubmitting && (
                          <i className="feather-loader spinner-icon"></i>
                        )}
                      </button>
                    </form>
                  </div>
                  <div className="signup-box-footer">
                    <div className="bottom-text">
                      ¿Ya tienes una cuenta?
                      <Link
                        className="btn-read-more ml--5"
                        href="/iniciar-sesion"
                      >
                        <span>Iniciar sesión</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Link className="close-button" href="/">
          <i className="fa-sharp fa-regular fa-x"></i>
        </Link>
      </div>
    </main>
  );
};

export default ForgotPassword;
